/**
 * The task side panel — always on screen, so it IS the task inbox: my open tasks grouped
 * by urgency with new-activity dots, expandable cards showing the latest comments with
 * inline reply + complete, a Watching section (cc'd tasks), the verify-queue count, and
 * quick task creation (defaults to yourself = a reminder; pick anyone from ServiceTitan's
 * employee list to delegate). Polls every 30s.
 *
 * Unclaimed MISSED CALLS (the shared Missed calls pool — the owner, 2026-09-28:
 * "easy and obvious for them to know when to call someone back") get their own
 * section at the top, each with a Claim. Their count — with stuck calls and
 * unassigned texts — is the alert strip above the header (alert-strip.tsx).
 *
 * OUTBOUND CALLS (the owner, 2026-10-07): a member of the Outbound calls pool
 * sees ONE "Claim Outbound Call" per active call list with customers left;
 * clicking it takes the next customer and opens their task. A list whose
 * quota for the day / week / month is reached shows when it comes back, with
 * no claim (the server refuses one anyway).
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { AlertStrip, refreshAlertCounts } from '#app/components/alert-strip.tsx'
import { PersonPicker } from '#app/components/person-picker.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { OUTBOUND_CALLS_DEPARTMENT } from '#app/services/outbound/types.ts'
import { cn } from '#app/utils/misc.tsx'

const TASK_PANEL_REFRESH_EVENT = 'hep:task-panel-refresh'

/**
 * Re-read the task panel now (and the alert strip with it) — for a page that
 * just changed one of the panel's tasks outside the panel itself, e.g. an
 * outbound call classified on its task page.
 */
export function refreshTaskPanel(): void {
	refreshAlertCounts()
	window.dispatchEvent(new Event(TASK_PANEL_REFRESH_EVENT))
}

type PanelTask = {
	id: number
	title: string
	priority: string
	status: string
	/** null = an unclaimed department-pool task */
	assignee_email: string | null
	created_by: string
	department: string | null
	due_at: string | null
	created_at?: string | null
	/** a missed call's own time (its call ended) — the panel sends it */
	missed_at?: string | null
	overdue?: boolean
	has_updates?: boolean
	requires_verification: boolean
	subtasks_total?: number
	subtasks_done?: number
	/** a pool task I may put back in its pool (claimed by mistake) */
	can_unclaim?: boolean
}
type Panel = {
	me: string
	verifyCount: number
	mine: Array<PanelTask>
	watching: Array<PanelTask>
	pool?: Array<PanelTask>
	/** unclaimed tasks in the shared Missed calls pool */
	missedCalls?: Array<PanelTask>
	/** one per active outbound call list with customers left (pool members) */
	outbound?: Array<OutboundItem>
	people: Array<{ email: string; name: string | null }>
}
type OutboundItem = {
	list_id: number
	name: string
	queued: number
	held_task_id: number | null
	quota_reached: {
		by: 'calls' | 'booked'
		resets_at: string
		resumes: string
	} | null
}
type Detail = {
	comments: Array<{
		id: number
		author: string
		body: string
		kind: string
		created_at: string
	}>
}

const PRIORITY_DOTS: Record<string, string> = {
	low: 'bg-neutral-400',
	normal: 'bg-sky-500',
	high: 'bg-amber-500',
	urgent: 'bg-hep-red',
}

function person(email: string | null | undefined) {
	return email ? (email.split('@')[0] ?? email) : ''
}
function fmtDue(iso: string | null) {
	if (!iso) return ''
	const d = new Date(iso)
	const today = d.toDateString() === new Date().toDateString()
	return d.toLocaleString('en-US', {
		...(today ? {} : { month: 'short', day: 'numeric' }),
		hour: 'numeric',
		minute: '2-digit',
	})
}

export function TaskSidebar() {
	const [panel, setPanel] = useState<Panel | null>(null)
	const [error, setError] = useState(false)
	const [showNew, setShowNew] = useState(false)
	// "Someone else claimed this call." — a lost claim race, said out loud
	const [claimLost, setClaimLost] = useState<string | null>(null)
	useEffect(() => {
		if (!claimLost) return
		const t = setTimeout(() => setClaimLost(null), 8_000)
		return () => clearTimeout(t)
	}, [claimLost])

	const load = useCallback(async () => {
		try {
			const res = await fetch('/resources/taskcenter-panel')
			if (!res.ok) throw new Error()
			setPanel((await res.json()) as Panel)
			setError(false)
		} catch {
			setError(true)
		}
	}, [])

	useEffect(() => {
		void load()
		const t = setInterval(() => {
			if (document.visibilityState === 'visible') void load()
		}, 30_000)
		// a page changed something the panel lists (an outbound call classified)
		const onRefresh = () => void load()
		window.addEventListener(TASK_PANEL_REFRESH_EVENT, onRefresh)
		return () => {
			clearInterval(t)
			window.removeEventListener(TASK_PANEL_REFRESH_EVENT, onRefresh)
		}
	}, [load])

	// something in the panel changed (a claim, a completion, a comment, a new
	// task): reload the panel, and have the alert strip re-read its counts now
	const changed = useCallback(() => {
		refreshAlertCounts()
		void load()
	}, [load])

	const mine = panel?.mine ?? []
	const overdue = mine.filter((t) => t.overdue)
	const updates = mine.filter((t) => t.has_updates && !t.overdue)
	const rest = mine.filter((t) => !t.overdue && !t.has_updates)
	const pool = panel?.pool ?? []
	const missedCalls = panel?.missedCalls ?? []
	const outbound = panel?.outbound ?? []
	const claimable = outbound.filter((o) => !o.quota_reached)
	// the person a missed call was for is cc'd on it: list it once, not twice
	const listed = new Set([...mine, ...pool, ...missedCalls].map((t) => t.id))
	const watching = (panel?.watching ?? []).filter((t) => !listed.has(t.id))

	return (
		<aside
			aria-label="My tasks"
			className="bg-card w-full shrink-0 border-b border-black/70 md:w-80 md:border-r md:border-b-0 lg:w-88"
		>
			<div className="flex flex-col md:sticky md:top-[calc(3.5rem+var(--banner-h,0px))] md:max-h-[calc(100vh-3.5rem-var(--banner-h,0px))]">
				<AlertStrip />
				<header className="bg-hep-black flex items-center gap-2 px-3 py-2 text-white">
					<Link
						to="/tasks"
						className="bg-hep-yellow text-hep-yellow-foreground flex items-center gap-2 rounded-full px-4 py-1.5 text-base leading-none font-black"
					>
						Tasks
					</Link>
					{panel && panel.verifyCount > 0 ? (
						<Link
							to="/tasks?tab=verify"
							className="rounded-full bg-amber-400 px-2 py-1 text-xs leading-none font-bold text-black"
							title="completed tasks waiting on your verification"
						>
							{panel.verifyCount} to verify
						</Link>
					) : null}
					<button
						type="button"
						onClick={() => setShowNew((s) => !s)}
						className="ml-auto flex items-center gap-1 rounded-full border border-white/40 px-3 py-1 text-xs font-bold"
					>
						<Icon name="plus" size="xs" /> Task
					</button>
				</header>

				<div className="grid grid-cols-2 border-b border-black/70 text-center text-xs font-bold">
					<span className="bg-white px-2 py-1 text-black">
						{mine.length} open
					</span>
					<span
						className={cn(
							'px-2 py-1',
							overdue.length
								? 'bg-hep-red text-white'
								: 'bg-neutral-200 text-neutral-600',
						)}
					>
						{overdue.length} behind schedule
					</span>
				</div>

				<div className="flex-1 space-y-3 overflow-y-auto p-3">
					{error ? (
						<p className="border-destructive/40 bg-destructive/5 rounded-md border px-2 py-1.5 text-xs">
							Couldn't reach the task service — retrying…
						</p>
					) : null}

					{showNew && panel ? (
						<QuickCreate
							panel={panel}
							onDone={() => {
								setShowNew(false)
								changed()
							}}
						/>
					) : null}

					{claimLost ? (
						<p
							role="status"
							className="rounded-md border border-amber-500/60 bg-amber-500/10 px-2 py-1.5 text-xs font-semibold"
						>
							{claimLost}
						</p>
					) : null}

					{missedCalls.length ? (
						<Section label="Missed calls — call them back" tone="red">
							{missedCalls.map((t) => (
								<PoolCard
									key={t.id}
									t={t}
									onChanged={changed}
									onClaimLost={setClaimLost}
									missedCall
								/>
							))}
						</Section>
					) : null}

					{outbound.length ? (
						<Section label="Outbound calls">
							{outbound.map((o) => (
								<OutboundClaimCard
									key={o.list_id}
									item={o}
									onClaimed={changed}
									onRefused={(message) => {
										setClaimLost(message)
										changed()
									}}
								/>
							))}
						</Section>
					) : null}

					{panel &&
					mine.length === 0 &&
					missedCalls.length === 0 &&
					claimable.length === 0 ? (
						<div className="rounded-lg border border-dashed px-3 py-6 text-center">
							<p className="text-2xl">🥳</p>
							<p className="mt-1 text-sm font-semibold">
								Congratulations — you're all caught up!
							</p>
							<p className="text-muted-foreground mt-0.5 text-xs">
								New tasks will appear here the moment they land.
							</p>
						</div>
					) : null}

					{overdue.length ? (
						<Section label="Behind schedule" tone="red">
							{overdue.map((t) => (
								<TaskCard key={t.id} t={t} me={panel!.me} onChanged={changed} />
							))}
						</Section>
					) : null}
					{updates.length ? (
						<Section label="New activity" tone="amber">
							{updates.map((t) => (
								<TaskCard key={t.id} t={t} me={panel!.me} onChanged={changed} />
							))}
						</Section>
					) : null}
					{rest.length ? (
						<Section
							label={
								overdue.length || updates.length
									? 'Everything else'
									: 'My tasks'
							}
						>
							{rest.map((t) => (
								<TaskCard key={t.id} t={t} me={panel!.me} onChanged={changed} />
							))}
						</Section>
					) : null}

					{pool.length ? (
						<Section label="Department pool" tone="amber">
							{pool.map((t) => (
								<PoolCard
									key={t.id}
									t={t}
									onChanged={changed}
									onClaimLost={setClaimLost}
								/>
							))}
						</Section>
					) : null}

					{watching.length ? (
						<Section label="Watching (cc)">
							{watching.map((t) => (
								<TaskCard
									key={t.id}
									t={t}
									me={panel!.me}
									onChanged={changed}
									watching
								/>
							))}
						</Section>
					) : null}
				</div>
			</div>
		</aside>
	)
}

function Section({
	label,
	tone,
	children,
}: {
	label: string
	tone?: 'red' | 'amber'
	children: React.ReactNode
}) {
	return (
		<section>
			<h3
				className={cn(
					'mb-1 text-[11px] font-bold tracking-[0.15em] uppercase',
					tone === 'red'
						? 'text-hep-red'
						: tone === 'amber'
							? 'text-amber-600 dark:text-amber-400'
							: 'text-muted-foreground',
				)}
			>
				{label}
			</h3>
			<ul className="space-y-2">{children}</ul>
		</section>
	)
}

function TaskCard({
	t,
	me,
	onChanged,
	watching = false,
}: {
	t: PanelTask
	me: string
	onChanged: () => void
	watching?: boolean
}) {
	const [open, setOpen] = useState(false)
	const [detail, setDetail] = useState<Detail | null>(null)
	const [comment, setComment] = useState('')
	const [busy, setBusy] = useState(false)

	async function expand() {
		const next = !open
		setOpen(next)
		if (next) {
			try {
				const res = await fetch(`/resources/taskcenter-panel?detail=${t.id}`)
				// detail comes from the full task endpoint via the tasks page loader instead;
				// cheap approach: reuse the task page API through this same resource
				if (res.ok) setDetail((await res.json()) as Detail)
			} catch {
				/* the expand still shows actions */
			}
		}
	}

	async function act(intent: string, extra?: Record<string, string>) {
		setBusy(true)
		const fd = new FormData()
		fd.set('intent', intent)
		fd.set('task', String(t.id))
		for (const [k, v] of Object.entries(extra ?? {})) fd.set(k, v)
		await fetch('/resources/taskcenter-panel', { method: 'POST', body: fd })
		setBusy(false)
		setComment('')
		onChanged()
	}

	return (
		<li
			className={cn(
				'rounded-lg border',
				t.overdue
					? 'border-hep-red/60'
					: 'border-black/30 dark:border-white/20',
			)}
		>
			<button
				type="button"
				onClick={() => void expand()}
				className="flex w-full items-start gap-2 px-2.5 py-2 text-left"
			>
				<span
					className={cn(
						'mt-1.5 size-2 shrink-0 rounded-full',
						PRIORITY_DOTS[t.priority] ?? 'bg-neutral-400',
					)}
					title={`priority ${t.priority}`}
				/>
				<span className="min-w-0 flex-1">
					<span className="block text-sm leading-tight font-semibold">
						{t.has_updates ? (
							<span
								className="bg-hep-yellow mr-1 inline-block size-2 rounded-full"
								title="new activity"
							/>
						) : null}
						{t.title}
					</span>
					<span className="text-muted-foreground block text-[11px]">
						{watching
							? `${person(t.assignee_email)} · ${t.status.replace(/_/g, ' ')}`
							: t.created_by !== me
								? `from ${person(t.created_by)}`
								: 'reminder'}
						{(t.subtasks_total ?? 0) > 0
							? ` · ${t.subtasks_done}/${t.subtasks_total}`
							: ''}
					</span>
				</span>
				{t.due_at ? (
					<span
						className={cn(
							'text-[11px] whitespace-nowrap tabular-nums',
							t.overdue ? 'text-hep-red font-bold' : 'text-muted-foreground',
						)}
					>
						{fmtDue(t.due_at)}
					</span>
				) : null}
			</button>
			{open ? (
				<div className="space-y-2 border-t px-2.5 py-2">
					{detail?.comments?.length ? (
						<ul className="space-y-1">
							{detail.comments.slice(-3).map((c) => (
								<li key={c.id} className="text-xs">
									<span className="font-bold">{person(c.author)}:</span>{' '}
									<span
										className={cn(
											c.kind !== 'comment' && 'text-muted-foreground italic',
										)}
									>
										{c.body}
									</span>
								</li>
							))}
						</ul>
					) : null}
					<div className="flex gap-1">
						<input
							value={comment}
							onChange={(e) => setComment(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && comment.trim())
									void act('comment', { body: comment })
							}}
							placeholder="Quick comment…"
							className="border-input bg-background h-8 min-w-0 flex-1 rounded-md border px-2 text-xs"
						/>
						{comment.trim() ? (
							<Button
								size="sm"
								className="h-8 px-2 text-xs"
								disabled={busy}
								onClick={() => void act('comment', { body: comment })}
							>
								Send
							</Button>
						) : null}
					</div>
					<div className="flex items-center justify-between gap-2">
						<Link
							to={`/tasks/${t.id}`}
							className="text-muted-foreground text-xs underline-offset-2 hover:underline"
						>
							Open full task →
						</Link>
						{watching ? null : t.department === OUTBOUND_CALLS_DEPARTMENT ? (
							// an outbound call closes when it is classified, on its page
							<Button
								asChild
								size="sm"
								variant="secondary"
								className="h-7 px-2 text-xs"
							>
								<Link to={`/tasks/${t.id}`}>Classify the call →</Link>
							</Button>
						) : (
							<Button
								size="sm"
								variant="secondary"
								className="h-7 px-2 text-xs"
								disabled={busy}
								onClick={() => void act('complete')}
							>
								<Icon name="check" size="xs" className="mr-1" />
								{t.requires_verification ? 'Complete → verify' : 'Complete'}
							</Button>
						)}
					</div>
					{!watching && t.can_unclaim ? (
						<div className="flex justify-end">
							<Button
								size="sm"
								variant="outline"
								className="h-7 px-2 text-xs"
								disabled={busy}
								title="Put this back in the pool for someone else to claim"
								onClick={() => void act('unclaim')}
							>
								Unclaim
							</Button>
						</div>
					) : null}
				</div>
			) : null}
		</li>
	)
}

/** An unclaimed pool task: title, department, due — and a one-click Claim. */
function PoolCard({
	t,
	onChanged,
	onClaimLost,
	missedCall = false,
}: {
	t: PanelTask
	onChanged: () => void
	/** somebody else claimed it first: what to tell this person */
	onClaimLost: (message: string) => void
	/** a missed call: say when it was missed, not which pool or who wrote it */
	missedCall?: boolean
}) {
	const [busy, setBusy] = useState(false)
	async function claim() {
		setBusy(true)
		const fd = new FormData()
		fd.set('intent', 'claim')
		fd.set('task', String(t.id))
		const res = await fetch('/resources/taskcenter-panel', {
			method: 'POST',
			body: fd,
		})
		if (res.status === 409) {
			onClaimLost(
				missedCall
					? 'Someone else claimed this call.'
					: 'Someone else claimed this task.',
			)
		}
		setBusy(false)
		onChanged()
	}
	return (
		<li className="rounded-lg border border-black/30 dark:border-white/20">
			<div className="flex items-start gap-2 px-2.5 py-2">
				<span
					className={cn(
						'mt-1.5 size-2 shrink-0 rounded-full',
						PRIORITY_DOTS[t.priority] ?? 'bg-neutral-400',
					)}
					title={`priority ${t.priority}`}
				/>
				<span className="min-w-0 flex-1">
					<Link
						to={`/tasks/${t.id}`}
						className="block text-sm leading-tight font-semibold hover:underline"
					>
						{t.title}
					</Link>
					<span className="text-muted-foreground block text-[11px]">
						{missedCall ? (
							`missed ${fmtDue(t.missed_at ?? t.created_at ?? null)}`
						) : (
							<>
								{t.department ? `${t.department} pool` : 'pool'} · from{' '}
								{person(t.created_by)}
								{t.due_at ? ` · due ${fmtDue(t.due_at)}` : ''}
							</>
						)}
					</span>
				</span>
				<Button
					size="sm"
					variant="secondary"
					className="h-7 px-2 text-xs"
					disabled={busy}
					onClick={() => void claim()}
					title="Take this task — first come, first served"
				>
					Claim
				</Button>
			</div>
		</li>
	)
}

/**
 * "Claim Outbound Call" for one list: the next customer becomes my task and
 * its page opens. A refusal (the list paused, emptied or at its quota since
 * the panel was drawn) is said out loud and the panel reloads.
 */
function OutboundClaimCard({
	item,
	onClaimed,
	onRefused,
}: {
	item: OutboundItem
	/** the claim went through: the panel re-reads (my new task, the count) */
	onClaimed: () => void
	onRefused: (message: string) => void
}) {
	const navigate = useNavigate()
	const [busy, setBusy] = useState(false)
	if (item.quota_reached) {
		return (
			<li className="rounded-lg border border-dashed border-black/30 px-2.5 py-2 dark:border-white/20">
				<span className="block text-sm leading-tight font-semibold">
					{item.name}
				</span>
				<span className="text-muted-foreground block text-[11px]">
					Quota reached — resumes {item.quota_reached.resumes}
				</span>
			</li>
		)
	}
	async function claim() {
		setBusy(true)
		const fd = new FormData()
		fd.set('intent', 'claim')
		fd.set('list', String(item.list_id))
		try {
			const res = await fetch('/resources/outbound', {
				method: 'POST',
				body: fd,
			})
			const body = (await res.json().catch(() => ({}))) as {
				task_id?: number
				detail?: string
			}
			if (res.ok && body.task_id) {
				onClaimed()
				await navigate(`/tasks/${body.task_id}`)
				return
			}
			onRefused(
				body.detail
					? `Outbound call not claimed: ${body.detail}.`
					: 'Outbound call not claimed — try again.',
			)
		} catch {
			onRefused('Outbound call not claimed — try again.')
		} finally {
			setBusy(false)
		}
	}
	return (
		<li className="rounded-lg border-2 border-black/60 dark:border-white/40">
			<div className="flex items-start gap-2 px-2.5 py-2">
				<Icon name="phone" size="sm" className="mt-0.5 shrink-0" />
				<span className="min-w-0 flex-1">
					<span className="block text-sm leading-tight font-bold">
						Claim Outbound Call
					</span>
					<span className="text-muted-foreground block text-[11px]">
						{item.name} · {item.queued.toLocaleString('en-US')} left
					</span>
				</span>
				{item.held_task_id ? (
					<Button
						asChild
						size="sm"
						variant="secondary"
						className="h-7 px-2 text-xs"
					>
						<Link
							to={`/tasks/${item.held_task_id}`}
							title="You have a call from this list still to classify"
						>
							Continue
						</Link>
					</Button>
				) : (
					<Button
						size="sm"
						className="h-7 px-2 text-xs"
						disabled={busy}
						onClick={() => void claim()}
						title="Take the next customer on this list"
					>
						Claim
					</Button>
				)}
			</div>
		</li>
	)
}

function QuickCreate({ panel, onDone }: { panel: Panel; onDone: () => void }) {
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	async function submit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault()
		setBusy(true)
		setError(null)
		const fd = new FormData(e.currentTarget)
		fd.set('intent', 'create')
		try {
			const res = await fetch('/resources/taskcenter-panel', {
				method: 'POST',
				body: fd,
			})
			if (!res.ok) throw new Error()
			onDone()
		} catch {
			setError("Couldn't create the task — check the fields and try again.")
		} finally {
			setBusy(false)
		}
	}
	return (
		<form onSubmit={submit} className="space-y-2 rounded-lg border p-2.5">
			<input
				name="title"
				required
				placeholder="What needs doing?"
				className="border-input bg-background h-9 w-full rounded-md border px-2 text-sm"
			/>
			<div className="grid grid-cols-2 gap-2">
				<PersonPicker
					name="assignee_email"
					label="Assign to"
					placeholder="me (reminder)"
					people={panel.people.filter((pp) => pp.email !== panel.me)}
					options={[{ value: panel.me, label: 'me (reminder)' }]}
					defaultValue={panel.me}
					clearable={false}
				/>
				<input
					name="due_at"
					type="datetime-local"
					className="border-input bg-background h-9 w-full rounded-md border px-1 text-xs"
				/>
			</div>
			{panel.people.length === 0 ? (
				<p className="text-muted-foreground text-[11px]">
					No assignable people yet — the staff roster hasn't synced (ETL).
				</p>
			) : null}
			{error ? (
				<p className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-2 py-1.5 text-xs">
					{error}
				</p>
			) : null}
			<div className="flex items-center justify-between gap-2">
				<label className="text-muted-foreground flex items-center gap-1 text-[11px]">
					<input type="checkbox" name="requires_verification" /> verify on
					completion
				</label>
				<Button type="submit" size="sm" disabled={busy}>
					Create
				</Button>
			</div>
			<p className="text-muted-foreground text-[11px]">
				Need CC, projects, or files? Use{' '}
				<Link to="/tasks" className="underline">
					the full form
				</Link>
				.
			</p>
		</form>
	)
}
